/**
 * Yugen Dollhouse Guide
 * One static hero. Pills inspect details via description + optional lens.
 * Scoped to <yugen-dollhouse-guide>. No 360 / WebGL.
 */
(function () {
  if (customElements.get('yugen-dollhouse-guide')) return;

  var COPY_MS = 360;
  var LENS_MS = 480;

  class YugenDollhouseGuide extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-ydg-tab]')];
      this.copyItems = [...this.querySelectorAll('[data-ydg-copy]')];
      this.navTrack = this.querySelector('[data-ydg-nav-track]');
      this.copyStage = this.querySelector('[data-ydg-copy-stage]');
      this.imageStage = this.querySelector('[data-ydg-image-stage]');
      this.heroImg = this.querySelector('.ydg__img');
      this.lens = this.querySelector('[data-ydg-lens]');
      this.lensImg = this.querySelector('[data-ydg-lens-img]');

      this.total = Math.min(this.tabs.length, this.copyItems.length);
      if (this.total === 0) return;

      this.activeIndex = -1;
      this.copyTimer = null;
      this.magnifierEnabled = this.getAttribute('data-ydg-magnifier') === 'true';
      this.magnifierMobileOff = this.getAttribute('data-ydg-magnifier-mobile-off') === 'true';
      this.defaultZoomTenths = Number(this.getAttribute('data-ydg-default-zoom') || 20);
      this.lensSrc = this.getAttribute('data-ydg-lens-src') || '';
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mobileQuery = window.matchMedia('(max-width: 749px)');

      this.onTabClick = this.onTabClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onMobileChange = this.onMobileChange.bind(this);

      this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick));
      if (this.navTrack) this.navTrack.addEventListener('keydown', this.onKeydown);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      window.addEventListener('resize', this.onResize, { passive: true });
      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.onMobileChange);
      } else if (this.mobileQuery.addListener) {
        this.mobileQuery.addListener(this.onMobileChange);
      }

      if (this.lensImg && this.lensSrc && !this.lensImg.getAttribute('src')) {
        this.lensImg.src = this.lensSrc;
      }

      this.clearSelection({ instant: true });
    }

    disconnectedCallback() {
      this.tabs.forEach((tab) => tab.removeEventListener('click', this.onTabClick));
      if (this.navTrack) this.navTrack.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      window.removeEventListener('resize', this.onResize);
      if (this.mobileQuery.removeEventListener) {
        this.mobileQuery.removeEventListener('change', this.onMobileChange);
      } else if (this.mobileQuery.removeListener) {
        this.mobileQuery.removeListener(this.onMobileChange);
      }
      if (this.copyTimer) clearTimeout(this.copyTimer);
    }

    onResize() {
      if (this.activeIndex >= 0) this.syncLens(this.activeIndex, { instant: true });
    }

    onMobileChange() {
      if (this.activeIndex >= 0) this.syncLens(this.activeIndex, { instant: true });
      else this.hideLens(true);
    }

    onTabClick(event) {
      const index = Number(event.currentTarget.dataset.index);
      if (Number.isNaN(index)) return;

      if (index === this.activeIndex) {
        this.clearSelection({ scrollTab: false });
        return;
      }

      this.selectStory(index, { scrollTab: true });
    }

    onKeydown(event) {
      if (!this.tabs.length) return;

      let next = this.activeIndex;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = this.activeIndex < 0 ? 0 : (this.activeIndex + 1) % this.total;
          event.preventDefault();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next =
            this.activeIndex < 0
              ? this.total - 1
              : (this.activeIndex - 1 + this.total) % this.total;
          event.preventDefault();
          break;
        case 'Home':
          next = 0;
          event.preventDefault();
          break;
        case 'End':
          next = this.total - 1;
          event.preventDefault();
          break;
        case 'Escape':
          if (this.activeIndex >= 0) {
            event.preventDefault();
            this.clearSelection();
            this.tabs[0].focus();
          }
          return;
        default:
          return;
      }

      this.selectStory(next, { scrollTab: true });
      this.tabs[next].focus();
    }

    onBlockSelect(event) {
      const el = event.target.closest('[data-ydg-tab]');
      if (!el) return;
      const index = Number(el.dataset.index);
      if (Number.isNaN(index)) return;
      this.selectStory(index, { instant: true, scrollTab: true });
    }

    selectStory(index, options = {}) {
      if (index < 0 || index >= this.total) return;
      if (index === this.activeIndex && !options.instant) return;

      const previousIndex = this.activeIndex;
      this.activeIndex = index;
      this.classList.remove('is-idle');
      if (this.copyStage) this.copyStage.classList.remove('is-idle');

      this.syncTabs(index, options);
      this.crossfadeCopy(index, previousIndex, options);
      this.syncLens(index, options);
    }

    clearSelection(options = {}) {
      const previousIndex = this.activeIndex;
      this.activeIndex = -1;
      this.classList.add('is-idle');
      if (this.copyStage) this.copyStage.classList.add('is-idle');

      this.syncTabs(-1, options);
      this.crossfadeCopy(-1, previousIndex, options);
      this.hideLens(options.instant || this.reduceMotion);
    }

    syncTabs(index, options = {}) {
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected || (index < 0 && i === 0) ? 0 : -1;

        if (selected && options.scrollTab) {
          this.scrollTabIntoView(tab);
        }
      });
    }

    scrollTabIntoView(tab) {
      const track = this.navTrack;
      if (!tab || !track) return;

      const trackRect = track.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      const edge = 16;
      let nextLeft = track.scrollLeft;

      if (tabRect.left < trackRect.left + edge) {
        nextLeft -= trackRect.left + edge - tabRect.left;
      } else if (tabRect.right > trackRect.right - edge) {
        nextLeft += tabRect.right - (trackRect.right - edge);
      } else {
        return;
      }

      track.scrollTo({
        left: nextLeft,
        behavior: this.reduceMotion ? 'auto' : 'smooth',
      });
    }

    setInert(el, inert) {
      if ('inert' in el) el.inert = inert;
    }

    isMobile() {
      return this.mobileQuery.matches;
    }

    magnifierAllowed() {
      if (!this.magnifierEnabled || !this.lens || !this.lensSrc) return false;
      if (this.magnifierMobileOff && this.isMobile()) return false;
      return true;
    }

    readLensConfig(tab) {
      if (!tab) return null;
      if (tab.getAttribute('data-ydg-mag') !== 'true') return null;

      const mobile = this.isMobile();
      const pick = (desktopKey, mobileKey) => {
        if (mobile && tab.hasAttribute(mobileKey)) {
          return Number(tab.getAttribute(mobileKey));
        }
        return Number(tab.getAttribute(desktopKey));
      };

      const zoomTenths = Number(tab.getAttribute('data-ydg-zoom') || this.defaultZoomTenths);
      return {
        focalX: pick('data-ydg-focal-x', 'data-ydg-focal-x-mobile') / 100,
        focalY: pick('data-ydg-focal-y', 'data-ydg-focal-y-mobile') / 100,
        lensX: pick('data-ydg-lens-x', 'data-ydg-lens-x-mobile') / 100,
        lensY: pick('data-ydg-lens-y', 'data-ydg-lens-y-mobile') / 100,
        zoom: Math.max(1.5, Math.min(3, zoomTenths / 10)),
      };
    }

    getLensSize() {
      if (!this.lens) return 128;
      const styles = getComputedStyle(this.lens);
      return parseFloat(styles.getPropertyValue('--ydg-lens-size')) || (this.isMobile() ? 128 : 180);
    }

    layoutLensImage(config) {
      if (!this.lens || !this.lensImg || !config || !this.imageStage || !this.heroImg) return;

      const stage = this.imageStage;
      const sourceImg = this.heroImg;
      const stageW = stage.clientWidth;
      const stageH = stage.clientHeight;
      const imgW = sourceImg.clientWidth || stageW;
      const imgH = sourceImg.clientHeight || stageH;
      if (!stageW || !imgW) return;

      const lensSize = this.getLensSize();
      const half = lensSize / 2;
      const imgLeft = sourceImg.offsetLeft;
      const imgTop = sourceImg.offsetTop;

      let lensCenterX = imgLeft + imgW * config.lensX;
      let lensCenterY = imgTop + imgH * config.lensY;
      lensCenterX = Math.min(Math.max(lensCenterX, half), stageW - half);
      lensCenterY = Math.min(Math.max(lensCenterY, half), stageH - half);

      this.lens.style.setProperty('--ydg-lens-x', `${(lensCenterX / stageW) * 100}%`);
      this.lens.style.setProperty('--ydg-lens-y', `${(lensCenterY / stageH) * 100}%`);

      const magW = imgW * config.zoom;
      const magH = imgH * config.zoom;
      const focalPx = magW * config.focalX;
      const focalPy = magH * config.focalY;

      this.lensImg.style.width = `${magW}px`;
      this.lensImg.style.height = `${magH}px`;
      this.lensImg.style.left = `${half - focalPx}px`;
      this.lensImg.style.top = `${half - focalPy}px`;
    }

    hideLens(instant) {
      if (!this.lens) return;
      this.lens.classList.remove('is-entering');
      this.lens.classList.add('is-hidden');
      this.lens.setAttribute('aria-hidden', 'true');

      if (instant) {
        const prev = this.lens.style.transition;
        this.lens.style.transition = 'none';
        void this.lens.offsetWidth;
        this.lens.style.transition = prev;
      }
    }

    showLens(config, options = {}) {
      if (!this.lens || !this.lensImg || !config) return;

      const wasHidden = this.lens.classList.contains('is-hidden');
      const instant = options.instant || this.reduceMotion;

      if (this.lensSrc && this.lensImg.getAttribute('src') !== this.lensSrc) {
        this.lensImg.src = this.lensSrc;
      }

      const apply = () => {
        this.layoutLensImage(config);
        this.lens.classList.remove('is-hidden', 'is-entering');
        this.lens.setAttribute('aria-hidden', 'true');
      };

      if (instant) {
        const prev = this.lens.style.transition;
        this.lens.style.transition = 'none';
        apply();
        void this.lens.offsetWidth;
        this.lens.style.transition = prev;
        return;
      }

      if (wasHidden) {
        this.lens.classList.add('is-entering');
        this.lens.classList.remove('is-hidden');
        this.layoutLensImage(config);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.lens.classList.remove('is-entering');
          });
        });
      } else {
        // Keep visible while gliding to the next detail
        this.lens.classList.remove('is-hidden', 'is-entering');
        this.layoutLensImage(config);
      }
    }

    syncLens(index, options = {}) {
      if (!this.magnifierAllowed() || index < 0) {
        this.hideLens(options.instant || this.reduceMotion);
        return;
      }

      const config = this.readLensConfig(this.tabs[index]);
      if (!config) {
        this.hideLens(options.instant || this.reduceMotion);
        return;
      }

      this.showLens(config, options);
    }

    finishCopy(index) {
      this.copyItems.forEach((item, i) => {
        item.classList.remove('is-leaving');
        const active = i === index;
        item.hidden = !active;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-hidden', active ? 'false' : 'true');
        this.setInert(item, !active);
      });

      if (this.copyStage) {
        this.copyStage.style.minHeight = '';
        if (index < 0) this.copyStage.classList.add('is-idle');
        else this.copyStage.classList.remove('is-idle');
      }
    }

    crossfadeCopy(index, previousIndex, options = {}) {
      const instant = options.instant || this.reduceMotion;

      if (this.copyTimer) {
        clearTimeout(this.copyTimer);
        this.copyTimer = null;
      }

      this.copyItems.forEach((item) => item.classList.remove('is-leaving'));

      if (instant || previousIndex === index) {
        this.finishCopy(index);
        return;
      }

      const nextItem = index >= 0 ? this.copyItems[index] : null;
      const prevItem = previousIndex >= 0 ? this.copyItems[previousIndex] : null;

      if (this.copyStage && prevItem && nextItem) {
        const prevHeight = prevItem.offsetHeight;
        const wasHidden = nextItem.hidden;
        nextItem.hidden = false;
        nextItem.classList.add('is-active');
        const nextHeight = nextItem.offsetHeight;
        nextItem.classList.remove('is-active');
        if (wasHidden) nextItem.hidden = true;
        this.copyStage.style.minHeight = Math.max(prevHeight, nextHeight, 0) + 'px';
      }

      if (prevItem) {
        prevItem.classList.remove('is-active');
        prevItem.classList.add('is-leaving');
        prevItem.setAttribute('aria-hidden', 'true');
        this.setInert(prevItem, true);
      }

      this.copyItems.forEach((item, i) => {
        if (i === index || item === prevItem) return;
        item.hidden = true;
        item.classList.remove('is-active', 'is-leaving');
        item.setAttribute('aria-hidden', 'true');
        this.setInert(item, true);
      });

      if (nextItem) {
        nextItem.hidden = false;
        nextItem.setAttribute('aria-hidden', 'false');
        this.setInert(nextItem, false);
        nextItem.classList.remove('is-active');

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            nextItem.classList.add('is-active');
          });
        });
      }

      if (this.copyStage && index >= 0) {
        this.copyStage.classList.remove('is-idle');
      }

      this.copyTimer = setTimeout(() => {
        this.finishCopy(index);
        this.copyTimer = null;
      }, COPY_MS);
    }
  }

  customElements.define('yugen-dollhouse-guide', YugenDollhouseGuide);
})();
